import { formatBrl } from '@estac1/shared';
import { type ChangeEvent, forwardRef, type SyntheticEvent, useState } from 'react';
import { cn } from '../lib/cn.js';
import { Input, type InputProps } from './input.js';

export interface MoneyInputProps extends Omit<InputProps, 'value' | 'defaultValue' | 'onChange'> {
  /** Value in cents. Pass `undefined` for empty. */
  value?: number;
  defaultValue?: number;
  /** Fired with the new value in cents (or 0 for empty). */
  onChange?: (cents: number) => void;
}

/**
 * Brazilian Real input with live cents-based mask.
 * The underlying value is always integer cents — no floats.
 *
 * UX: user types digits left-to-right and the input formats as if the cursor
 * sat at the cents column. So typing "1" -> "R$ 0,01", "12" -> "R$ 0,12",
 * "123" -> "R$ 1,23", etc. Backspace removes the rightmost digit.
 *
 * Por isso o cursor mora no fim (#619): o clique antes de "0,00" punha os
 * dígitos ali, e 1000 virava R$ 10.000,00. Uma seleção fica como está, para
 * selecionar tudo e digitar trocar o valor.
 */
export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(
  ({ value, defaultValue, onChange, onSelect, className, ...rest }, ref) => {
    const [internalCents, setInternalCents] = useState(defaultValue ?? value ?? 0);
    const cents = value ?? internalCents;
    const display = formatBrl(cents);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, '');
      const next = digits === '' ? 0 : Number.parseInt(digits, 10);
      const safe = Number.isFinite(next) && next >= 0 ? next : 0;
      if (value === undefined) setInternalCents(safe);
      onChange?.(safe);
    };

    const keepCaretAtEnd = (e: SyntheticEvent<HTMLInputElement>) => {
      const el = e.currentTarget;
      const end = el.value.length;
      if (el.selectionStart === el.selectionEnd && el.selectionEnd !== end) {
        el.setSelectionRange(end, end);
      }
      onSelect?.(e);
    };

    return (
      <Input
        ref={ref}
        type="text"
        inputMode="numeric"
        value={display}
        onChange={handleChange}
        onSelect={keepCaretAtEnd}
        className={cn('font-mono text-right tabular-nums', className)}
        {...rest}
      />
    );
  },
);
MoneyInput.displayName = 'MoneyInput';
